import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCurrency } from '../../../../context/CurrencyContext';
import { getServicesFromDB } from '../../../../services/pricingService';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { selectedCurrency, convertPrice, isLoading: currencyLoading } = useCurrency();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get the same default services as OurServices component
  const getDefaultServices = () => [
    {
      id: 1,
      slug: 'consultation',
      title: 'Doctor Consultation',
      description: 'Book appointments with top-rated specialists across all medical fields',
      basePrice: 500,
      baseCurrency: 'INR',
      features: [
        '24/7 appointment booking',
        'Verified doctor profiles',
        'Instant confirmation',
        'Reschedule flexibility'
      ],
      icon: '👨‍⚕️',
      category: 'consultation'
    },
    {
      id: 2,
      slug: 'management',
      title: 'Hospital Management',
      description: 'Comprehensive hospital management solutions for healthcare providers',
      basePrice: 2500,
      baseCurrency: 'INR',
      features: [
        'Patient management system',
        'Appointment scheduling',
        'Billing & invoicing',
        'Staff management'
      ],
      icon: '🏥',
      category: 'management'
    },
    {
      id: 3,
      slug: 'telemedicine',
      title: 'Telemedicine Services',
      description: 'Virtual consultations with healthcare professionals from anywhere',
      basePrice: 800,
      baseCurrency: 'INR',
      features: [
        'HD video calls',
        'Secure messaging',
        'Prescription delivery',
        'Medical records access'
      ],
      icon: '📱',
      category: 'telemedicine'
    },
    {
      id: 4,
      slug: 'insurance',
      title: 'Health Insurance',
      description: 'Comprehensive health coverage plans for individuals and families',
      basePrice: 1500,
      baseCurrency: 'INR',
      features: [
        'Multiple plan options',
        'Cashless hospitalization',
        'Pre & post hospitalization',
        '24/7 customer support'
      ],
      icon: '🛡️',
      category: 'insurance'
    },
    {
      id: 5,
      slug: 'lab',
      title: 'Laboratory Services',
      description: 'Home sample collection and lab testing services',
      basePrice: 300,
      baseCurrency: 'INR',
      features: [
        'Home sample collection',
        'Quick results delivery',
        'Certified laboratories',
        'Affordable pricing'
      ],
      icon: '🧪',
      category: 'lab'
    },
    {
      id: 6,
      slug: 'pharmacy',
      title: 'Pharmacy Delivery',
      description: 'Fast delivery of medicines and healthcare products',
      basePrice: 200,
      baseCurrency: 'INR',
      features: [
        'Same-day delivery',
        'Genuine medicines',
        'Prescription verification',
        'Discount offers'
      ],
      icon: '💊',
      category: 'pharmacy'
    }
  ];

  // Fetch services from database
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        console.log('ServiceDetail: Starting to fetch services for slug:', slug);
        const servicesData = await getServicesFromDB();
        
        console.log('Raw services data from DB in ServiceDetail:', servicesData);
        console.log('Looking for slug:', slug);
        
        if (servicesData && servicesData.length > 0) {
          // Validate the data structure
          const validServices = servicesData.filter(service => {
            const isValid = service && service.slug && service.title;
            if (!isValid) {
              console.warn('Invalid service data in ServiceDetail:', service);
            }
            return isValid;
          });
          
          if (validServices.length > 0) {
            console.log('Valid services found in ServiceDetail:', validServices);
            setServices(validServices);
          } else {
            console.log('No valid services found in DB, using default services');
            setServices(getDefaultServices());
          }
        } else {
          console.log('No services found in DB, using default services');
          setServices(getDefaultServices());
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices(getDefaultServices());
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [slug]);

  const service = useMemo(() => {
    console.log('Finding service with slug:', slug);
    console.log('Available services:', services);
    const foundService = services.find(s => s.slug === slug);
    console.log('Found service:', foundService);
    return foundService;
  }, [slug, services]);
  
  if (loading) {
    return (
      <div className="container" style={{ padding: '60px 0' }}>
        <h2>Loading...</h2>
        <p>Please wait while we fetch the service details.</p>
        <p>Slug: {slug}</p>
        <p>Services count: {services.length}</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="container" style={{ padding: '60px 0' }}>
        <h2>Service not found</h2>
        <p>The service you are looking for does not exist.</p>
        <p>Slug: {slug}</p>
        <p>Available services: {services.map(s => s.slug).join(', ')}</p>
        <Link to="/" style={{ color: '#2563eb', fontWeight: 600 }}>Go back</Link>
      </div>
    );
  }

  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹', 
      'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
    };
    return currencyMap[currency] || '₹';
  };

  return (
    <section className="our-services">
      <div className="container">
        <div className="section-header" style={{ marginBottom: 30 }}>
          <Link to="/" style={{ color: '#2563eb', fontWeight: 600, display: 'inline-block', marginBottom: '20px' }}>
            <i className="fas fa-arrow-left"></i> Back to Home
          </Link>
          <h1 className="section-title" style={{ fontSize: '2.5rem', marginBottom: '20px' }}>
            {service.icon} {service.title}
          </h1>
          <p className="section-description" style={{ maxWidth: 800, fontSize: '1.2rem', lineHeight: 1.6 }}>
            {service.description}
          </p>
          <div style={{ backgroundColor: '#f0f0f0', padding: '10px', marginTop: '10px', borderRadius: '5px' }}>
            <p><strong>Debug Info:</strong></p>
            <p>Slug: {slug}</p>
            <p>Service ID: {service.id}</p>
            <p>Service Title: {service.title}</p>
            <p>Features count: {service.features ? service.features.length : 0}</p>
            <p>Services loaded: {services.length}</p>
          </div>
        </div>

        <div className="service-card" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px' }}>
          <div className="service-content">
            <div className="service-icon" style={{ textAlign: 'center', marginBottom: '30px' }}>
              <span className="icon-emoji" style={{ fontSize: '4rem' }}>{service.icon}</span>
            </div>
            
            <h3 className="service-title" style={{ fontSize: '1.8rem', textAlign: 'center', marginBottom: '30px' }}>
              What you get with {service.title}
            </h3>
            
            <div className="service-features" style={{ maxHeight: 'unset', marginBottom: '40px' }}>
              {service.features.map((feature, index) => (
                <div key={index} className="feature-item" style={{ padding: '15px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <i className="fas fa-check" style={{ color: '#10b981', marginRight: '15px' }}></i>
                  <span style={{ fontSize: '1.1rem' }}>{feature}</span>
                </div>
              ))}
            </div>
            
            <div className="service-pricing" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 15, 
              marginBottom: '30px',
              padding: '20px',
              backgroundColor: '#f8fafc',
              borderRadius: '10px'
            }}>
              <div className="price" style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e293b' }}>
                {getCurrencySymbol(service.baseCurrency)}{service.basePrice}
              </div>
              <div className="price-period" style={{ fontSize: '1.1rem', color: '#64748b' }}>
                base price
              </div>
              {currencyLoading && <span style={{ marginLeft: '10px' }}>🔄</span>}
            </div>
            
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <button className="service-cta" style={{ 
                padding: '15px 30px', 
                fontSize: '1.1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: '15px'
              }}>
                <span>Get Started</span>
                <i className="fas fa-arrow-right" style={{ marginLeft: '10px' }}></i>
              </button>
              <button className="service-cta" style={{ 
                padding: '15px 30px', 
                fontSize: '1.1rem',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}>
                <span>Contact Sales</span>
                <i className="fas fa-phone" style={{ marginLeft: '10px' }}></i>
              </button>
            </div>
            
            <div className="service-links" style={{ justifyContent: 'center', textAlign: 'center' }}>
              <Link className="service-detail-link" to="/" style={{ 
                color: '#2563eb', 
                fontWeight: 600, 
                textDecoration: 'none',
                fontSize: '1.1rem'
              }}>
                ← Back to all services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceDetail;
